import { toast } from "sonner";
import * as Icons from "lucide-react";
import { api, apiError } from "../../lib/api";
import {
  useResource, PageTitle, Panel, Loading, ErrorState, EmptyState, Pill, DemoBadge, fmtDate,
} from "../../components/app/Kit";
import { useAuth } from "../../context/AuthContext";

const ROLES = ["owner", "admin", "manager", "staff"];

const PERMISSIONS = [
  ["Owner", "Full access, including billing, integrations and team roles."],
  ["Admin", "Everything except ownership transfer; can manage integrations and settings."],
  ["Manager", "Can manage leads, customers, appointments, tasks and automations."],
  ["Staff", "Can work assigned leads, customers, appointments and messages."],
  ["AI Agent", "Non-human actor; scope defined per agent in AI Agents."],
];

export default function Team() {
  const { user } = useAuth();
  const { data, state, error, reload } = useResource("/team/members");

  const changeRole = async (member, role) => {
    try {
      await api.patch(`/team/members/${member.user_id}/role`, { role });
      toast.success(`${member.name} is now ${role}.`);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const canEdit = user?.role === "owner" || user?.role === "admin" || user?.is_superadmin;

  return (
    <div data-testid="team-page">
      <PageTitle
        testId="team-title"
        title="Team"
        sub="Roles, permissions and workload — including AI agents as non-human team members."
        action={<DemoBadge />}
      />

      {state === "loading" ? <Loading rows={6} testId="team-loading" /> : null}
      {state === "error" ? <ErrorState message={error} onRetry={reload} testId="team-error" /> : null}
      {state === "ready" && data.length === 0 ? (
        <EmptyState icon="UserCog" title="No team members" sub="Invite your team during onboarding." testId="team-empty" />
      ) : null}

      {state === "ready" && data.length > 0 ? (
        <div className="grid gap-2">
          {data.map((m) => (
            <div key={m.user_id} data-testid={`team-member-${m.user_id}`} className="glass flex flex-wrap items-center justify-between gap-3 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-md text-[11px] font-bold ${
                    m.role === "ai_agent" ? "bg-elv-cyan/15 text-elv-cyan" : "bg-elv-green/15 text-elv-green"
                  }`}
                >
                  {m.role === "ai_agent" ? <Icons.Bot className="h-4 w-4" /> : m.name.slice(0, 1)}
                </span>
                <div>
                  <p className="text-xs font-medium text-elv-text">{m.name}</p>
                  <p className="mt-0.5 text-[11px] text-elv-dim">{m.email}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-[11px] text-elv-dim">
                  {m.assigned_leads} leads · {m.assigned_customers} customers
                </span>
                <Pill tone={m.status === "active" ? "green" : "amber"}>{m.status.replace(/_/g, " ")}</Pill>
                {m.role === "ai_agent" ? (
                  <Pill tone="cyan">AI agent</Pill>
                ) : canEdit ? (
                  <select
                    value={m.role}
                    onChange={(e) => changeRole(m, e.target.value)}
                    data-testid={`team-role-select-${m.user_id}`}
                    className="rounded-md border border-elv-line bg-[#0A0F14] px-2 py-1 text-[11px] text-elv-text outline-none"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Pill tone="dim">{m.role}</Pill>
                )}
                <span className="font-mono text-[10px] text-elv-dim">{fmtDate(m.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <Panel testId="team-permissions" title="Roles & permissions" className="mt-4">
        <div className="grid gap-2">
          {PERMISSIONS.map(([role, detail]) => (
            <div key={role} className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
              <p className="text-xs font-medium text-elv-text">{role}</p>
              <p className="mt-1.5 text-[11px] text-elv-dim">{detail}</p>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
